import LoadingSpinner from "@/components/common/LoadingSpinner";
import { CustomChildTable } from "@/components/CustomChildTable";
import { CustomImageUpload } from "@/components/CustomImageUpload";
import { CustomFileUpload } from "@/components/CustomFileUpload";
import { get } from "@/services/axiosUitils";
import PasswordInput from "@/components/PasswordInput";
import {
  Checkbox,
  DatePicker,
  FormGrid,
  FormItem,
  FormLayout,
  FormTab,
  Input,
  NumberPicker,
  Radio,
  Select,
  Switch,
  TimePicker,
  TreeSelect,
} from "@formily/antd-v5";
import { createSchemaField, FormProvider } from "@formily/react";
import {
  Card,
  Cascader,
  InputNumber,
  Rate,
  Slider,
  Tabs,
  Transfer,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { translateSchemaDeep } from "@/lib/translateSchemaDeep";
import GoogleAddressInput from "@/components/GoogleAddressInput";

const Text = ({ value, content, mode = "span", style = {}, ...props }: any) => {
  const Tag = mode ?? "span";
  return (
    <Tag style={style} {...props}>
      {content ?? value}
    </Tag>
  );
};

const SchemaField = createSchemaField({
  components: {
    FormItem,
    Input,
    NumberPicker,
    Password: PasswordInput,
    Select,
    Checkbox,
    DatePicker,
    Switch,
    Radio,
    CustomFileUpload,
    GoogleAddressInput,
    CustomImageUpload,
    FormLayout,
    Card,
    FormGrid,
    FormTab,
    Tabs,
    InputNumber,
    TreeSelect,
    TimePicker,
    Cascader,
    Transfer,
    Slider,
    Rate,
    TextArea,
    CustomChildTable,
    Text,
    ReactQuill,
  },
});

// Helper function to convert enum to dataSource for Select components
const convertEnumToDataSource = (schema: any, fieldName?: string): any => {
  if (!schema || typeof schema !== "object") {
    return schema;
  }

  if (Array.isArray(schema)) {
    return schema.map(item => convertEnumToDataSource(item, fieldName));
  }

  const result: any = { ...schema };

  // If this is a Select component with enum, ensure it's properly formatted
  // Formily's Select automatically handles enum, but we ensure the format is correct
  if (
    result["x-component"] === "Select" &&
    result.enum &&
    Array.isArray(result.enum)
  ) {
    // Ensure enum items are in the correct format (label/value objects)
    const formattedEnum = result.enum.map((item: any) => {
      if (typeof item === "string") {
        return { label: item, value: item };
      }
      if (item && typeof item === "object") {
        // Ensure it has both label and value
        return {
          label: item.label ?? item.title ?? item.value ?? "",
          value: item.value ?? item.label ?? "",
        };
      }
      return item;
    });

    // Update enum with formatted values
    result.enum = formattedEnum;

    // Also set dataSource as a fallback (Formily will use enum by default)
    if (!result.dataSource && !result["x-data-source"]) {
      result.dataSource = formattedEnum;
    }

    // Ensure the Select is enabled and interactive
    result["x-component-props"] ??= {};
    // Explicitly set these to ensure Select is interactive
    result["x-component-props"].disabled = false;
    result["x-component-props"].readOnly = false;
    result["x-component-props"].allowClear = true;
    result["x-component-props"].style = {
      pointerEvents: "auto",
      ...result["x-component-props"].style,
    };

    // Ensure field is not disabled or readonly
    if (result["x-disabled"] !== undefined) {
      delete result["x-disabled"];
    }
    if (result["x-readonly"] !== undefined) {
      delete result["x-readonly"];
    }

    console.log("🔄 Formatted enum for Select:", {
      field: result.name,
      enum: result.enum,
    });
  }

  // Override maxCount and multiple for specific fields
  if (
    fieldName === "banner" ||
    fieldName === "thumbnail" ||
    fieldName === "banner_url" ||
    fieldName === "thumbnail_url" ||
    fieldName === "banner_image" ||
    fieldName === "thumbnail_image"
  ) {
    if (
      result["x-component"] === "CustomFileUpload" ||
      result["x-component"] === "CustomImageUpload"
    ) {
      result["x-component-props"] = {
        ...result["x-component-props"],
        maxCount: 1,
        multiple: false,
      };
    }
  } else if (fieldName === "attachments") {
    if (
      result["x-component"] === "CustomFileUpload" ||
      result["x-component"] === "CustomImageUpload"
    ) {
      result["x-component-props"] = {
        ...result["x-component-props"],
        maxCount: 5,
      };
    }
  }

  // Filter out non-DOM props from x-component-props to prevent React warnings
  if (
    result["x-component-props"] &&
    typeof result["x-component-props"] === "object"
  ) {
    const filteredProps: any = {};
    const nonDomProps = ["is-in-grid", "is-title", "gridSpan"]; // Props that shouldn't go to DOM

    Object.entries(result["x-component-props"]).forEach(([key, value]) => {
      if (!nonDomProps.includes(key)) {
        filteredProps[key] = value;
      }
    });

    result["x-component-props"] = filteredProps;
  }

  // Recursively process properties (handles nested structures like FormGrid)
  if (result.properties && typeof result.properties === "object") {
    if (Array.isArray(result.properties)) {
      result.properties = result.properties.map(item =>
        convertEnumToDataSource(item)
      );
    } else {
      result.properties = Object.fromEntries(
        Object.entries(result.properties).map(([key, value]) => [
          key,
          convertEnumToDataSource(value, key),
        ])
      );
    }
  }

  // Also handle items array (for arrays of properties)
  if (result.items && Array.isArray(result.items)) {
    result.items = result.items.map(item =>
      convertEnumToDataSource(item, fieldName)
    );
  }

  return result;
};

const SchemaFormRender = ({ schemaJson, form }: any) => {
  const { t } = useTranslation();

  // Move hooks before any early returns
  const [translatedSchema, setTranslatedSchema] = useState(() => {
    if (!schemaJson) {
      return null;
    }
    const schema = schemaJson.schema ?? schemaJson;
    const translated = translateSchemaDeep(schema, t, "common");
    // Convert enum to dataSource for Select components
    return convertEnumToDataSource(translated);
  });

  const useAsyncDataSource =
    (service: (arg0: any) => Promise<any>, doc: any) => (field: any) => {
      field.loading = true;
      service(doc).then((data: any) => {
        field.dataSource = data;
        field.loading = false;
      });
    };

  const getOptions = async (schemaName: string) => {
    const data = await getOptionsApiCall(schemaName);
    return data;
  };

  async function getOptionsApiCall(schemaName: string) {
    return await get(`/dropdown/${schemaName}/`);
  }

  useEffect(() => {
    if (schemaJson) {
      const schema = schemaJson.schema ?? schemaJson;
      const translated = translateSchemaDeep(schema, t, "common");
      // Convert enum to dataSource for Select components
      setTranslatedSchema(convertEnumToDataSource(translated));
    }
  }, [schemaJson, t]);

  // Ensure Select fields are interactive after render
  useEffect(() => {
    if (!form || !translatedSchema) {
      return;
    }

    // Use a timeout to ensure fields are rendered
    const timeoutId = setTimeout(() => {
      try {
        // Try to access fields - Formily might use a different API
        // Check if form has a fields property or similar
        if (form.fields) {
          Object.values(form.fields).forEach((field: any) => {
            if (field && field.component === "Select") {
              // Ensure field is editable
              if (field.setEditable) {
                field.setEditable(true);
              }
              if (field.setReadOnly) {
                field.setReadOnly(false);
              }

              // Ensure dataSource is set if enum exists
              if (
                field.enum &&
                Array.isArray(field.enum) &&
                (!field.dataSource || field.dataSource.length === 0)
              ) {
                const dataSource = field.enum.map((item: any) => {
                  if (typeof item === "string") {
                    return { label: item, value: item };
                  }
                  if (item && typeof item === "object") {
                    return {
                      label: item.label ?? item.title ?? item.value ?? "",
                      value: item.value ?? item.label ?? "",
                    };
                  }
                  return item;
                });

                if (typeof field.setDataSource === "function") {
                  field.setDataSource(dataSource);
                } else {
                  field.dataSource = dataSource;
                }
              }
            }
          });
        }
      } catch (_error) {
        // Ignore errors - fields might not be accessible this way
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [form, translatedSchema]);

  if (!schemaJson) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const layoutProps = schemaJson.form ?? {};

  return (
    <FormProvider form={form}>
      <FormLayout {...layoutProps}>
        <SchemaField
          schema={translatedSchema}
          scope={{
            useAsyncDataSource,
            getOptions,
            t,
          }}
        />
      </FormLayout>
    </FormProvider>
  );
};

export default SchemaFormRender;
